import { useContext } from 'react';
import { useDropzone } from 'react-dropzone';

import { mergeClasses } from '@/utils';

import { AttachmentIcon } from '@/assets/icons';
import { Button } from '@/components/common';

import { RENDER_BYTES } from './Dropzone.constants';
import { DropzoneContext } from './Dropzone.contexts';
import { IDropzone } from './Dropzone.interfaces';

const Dropzone = ({
  accept,
  maxFiles = 1,
  maxSize,
  minSize,
  onDrop,
  onError,
  disabled,
  src,
  className,
  children,
  error,
  ...props
}: IDropzone) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    maxFiles,
    maxSize,
    minSize,
    onError,
    disabled,
    onDrop: (acceptedFiles, fileRejections, event) => {
      if (fileRejections.length > 0) {
        const message = fileRejections.at(0)?.errors.at(0)?.message;
        onError?.(new Error(message));
        return;
      }
      onDrop?.(acceptedFiles, fileRejections, event);
    },
    ...props,
  });
  return (
    <DropzoneContext.Provider
      key={JSON.stringify(src)}
      value={{ src, accept, maxSize, minSize, maxFiles }}
    >
      <div className="flex flex-col gap-1.5 w-full">
        <Button
          className={mergeClasses(
            'relative h-auto w-full flex-col overflow-hidden !p-9 rounded-[12px] !shadow-none',
            isDragActive
              ? 'outline-none !border-brand-primary-base !bg-brand-primary-lightest'
              : '!border-neutral-n4',
            error && '!border-semantic-error-base',
            className,
          )}
          disabled={disabled}
          type="button"
          variant="cancel"
          {...getRootProps()}
        >
          <input {...getInputProps()} disabled={disabled} />
          {children}
        </Button>
        {error && <p className="text-xs text-semantic-error-base">{error}</p>}
      </div>
    </DropzoneContext.Provider>
  );
};

const DropzoneEmptyState = ({ className }: { className?: string }) => {
  const context = useContext(DropzoneContext);
  if (!context) {
    throw new Error('useDropzoneContext must be used within a Dropzone');
  }
  const { accept, maxSize, minSize } = context;

  let captionType = '';
  let captionSize = '';

  if (accept) {
    captionType += 'Allowed file types: ';
    const extensions = Object.values(accept).flat();
    captionType += new Intl.ListFormat('en').format(extensions);
  }

  if (minSize && maxSize) {
    captionSize += `File size between ${RENDER_BYTES(minSize)} and ${RENDER_BYTES(maxSize)}`;
  } else if (minSize) {
    captionSize += `Minimum file size:  ${RENDER_BYTES(minSize)}`;
  } else if (maxSize) {
    captionSize += `Maximum file size:  ${RENDER_BYTES(maxSize)}`;
  }

  return (
    <div className={mergeClasses('flex flex-col items-center justify-center gap-4 p-0', className)}>
      <div className="flex items-center justify-center rounded-md bg-transparent">
        <AttachmentIcon className="size-8 text-neutral-n6" />
      </div>
      <div className="w-full truncate text-wrap text-base font-medium text-neutral-n7">
        Drag and drop your file here or{' '}
        <p className="inline text-brand-primary-base font-semibold underline">Browse to upload</p>
      </div>
      <div>
        {captionType && (
          <p className="text-wrap text-sm font-medium text-neutral-n6">{captionType}</p>
        )}
        {captionSize && (
          <p className="text-wrap text-sm font-medium text-neutral-n6">{captionSize}</p>
        )}
      </div>
    </div>
  );
};

export { Dropzone, DropzoneEmptyState };
